import Vue from 'vue'
import { HubConnectionBuilder, LogLevel, HttpTransportType } from '@microsoft/signalr'
import { $backEndUrl } from '@serverConfig'
// import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import EventBus from '../mixins/event-bus/eventBus'
import apiTermos from '../services/api-termos'

// Tipos de termos verificados ao fazer login e a data limite de criação para exibição
const TERMOS_LOGIN = [1]
const TERMOS_LOGIN_DATA_LIMITE = '2026-03-20'

// eslint-disable-next-line import/prefer-default-export
export function verificarTermosPendentesLogin() {
  apiTermos.getPendentes(TERMOS_LOGIN, TERMOS_LOGIN_DATA_LIMITE)
    .then(r => {
      if (r.data?.length) EventBus.$emit('termosPendentes', r.data)
      EventBus.$emit('termosPendentesVerificados', !!(r.data?.length))
    })
    .catch(() => {
      // Falha ao obter termos pendentes não impede fluxo de login
      EventBus.$emit('termosPendentesVerificados', false)
    })
}

// Singleton e Debounce mantidos no escopo do módulo ES6
let connection = null
let isConnecting = false
let vinculatedToken = null
const ultimosToasts = new Map()

const fallbackConnection = {
  stop: () => Promise.resolve(),
  invoke: () => Promise.resolve(),
  on: () => {},
  off: () => {},
  state: 'Disconnected',
}

function isConnectionActive(connec) {
  return connec && (
    connec.state === 'Connected'
    || connec.state === 'Connecting'
    || connec.state === 'Reconnecting'
  )
}

const toastTypes = {
  sucesso: { icon: 'fa-solid fa-circle-check', variant: 'success' },
  success: { icon: 'fa-solid fa-circle-check', variant: 'success' },
  erro: { icon: 'fa-solid fa-circle-exclamation', variant: 'danger' },
  error: { icon: 'fa-solid fa-circle-exclamation', variant: 'danger' },
  concluidocomerros: { icon: 'fa-solid fa-circle-check', variant: 'success' },
  aviso: { icon: 'fa-solid fa-triangle-exclamation', variant: 'warning' },
  warning: { icon: 'fa-solid fa-triangle-exclamation', variant: 'warning' },
  iniciado: { icon: 'fa-solid fa-hourglass-end', variant: 'success' },
}

function showToast(mensagem, tipo) {
  const config = toastTypes[tipo]

  if (!config) {
    return
  }

  const agora = Date.now()
  const chave = `${tipo}:${mensagem}`
  const ultimaExibicao = ultimosToasts.get(chave)
  if (ultimaExibicao && agora - ultimaExibicao < 3000) {
    return
  }
  ultimosToasts.set(chave, agora)

  if (ultimosToasts.size > 50) {
    ultimosToasts.forEach((timestamp, k) => {
      if (agora - timestamp > 10000) {
        ultimosToasts.delete(k)
      }
    })
  }

  Vue.$toast({
    component: ToastificationContent,
    props: { title: mensagem, ...config },
  })
}

function showModal({ title, message, type }) {
  Vue.swal({
    title,
    html: message,
    icon: type, // success | error | warning | info | question
    confirmButtonText: 'Ok',
    showCancelButton: false,
  })
}

// cria a conexão padrão com o backend
function createConnection() {
  return new HubConnectionBuilder()
    .configureLogging(LogLevel.Critical)
  // .configureLogging(LogLevel.Debug)
    .withUrl(`${$backEndUrl}/hubs/events`, {
      transport: HttpTransportType.LongPolling,
    })
    .build()
}

// inicia a conexão e retorna uma promise
function startConnection(connec) {
  return connec.start()
}

// define todos os eventos esperados do backend (caso sejam criados novos eventos lá, necessário incluir aqui para recebê-los)
function setEventHandlers(connec) {
  connec.off('conectado')
  connec.off('notificacao')
  connec.off('notificacaoOrModal')
  connec.off('atualizaBaixa')
  connec.off('atualizaObservacoes')
  connec.off('atualizaCertificadosVencidos')
  connec.off('atualizarPlano')
  connec.off('atualizarCredito')
  connec.off('termosPendentes')
  connec.off('atualizacaoProcuracaoFinalizada')

  // chamado logo após ser conectado ao hub
  connec.on('conectado', () => {
  })

  connec.on('notificacao', (mensagem, tipoNotificacao) => {
    showToast(mensagem, tipoNotificacao)
  })

  connec.on('notificacaoOrModal', dados => {
    if (dados.notificationType === 'modal') {
      showModal(dados)
    } else if (dados.notificationType === 'pop-pup') {
      showToast(dados.message, dados.type)
    }
  })

  // atualiza a lista de baixa em lote
  connec.on('atualizaBaixa', () => {
    EventBus.$emit('atualizaBaixa')
  })

  connec.on('atualizaObservacoes', () => {
    EventBus.$emit('atualizaObservacoes')
  })

  connec.on('atualizaCertificadosVencidos', () => {
    EventBus.$emit('atualizaCertificadosVencidos')
  })

  connec.on('atualizarPlano', novoPlano => {
    EventBus.$emit('atualizarPlano', novoPlano)
  })

  connec.on('atualizarCredito', credito => {
    EventBus.$emit('atualizarCredito', credito)
  })

  // Recebe termos pendentes enviados ativamente pelo backend
  connec.on('termosPendentes', termos => {
    EventBus.$emit('termosPendentes', termos)
  })

  connec.on('atualizacaoProcuracaoFinalizada', sucesso => {
    EventBus.$emit('atualizacaoProcuracaoFinalizada', sucesso)
  })
}

// função de conexão com a hub
async function start() {
  if (isConnectionActive(connection)) {
    if (localStorage.accessToken && localStorage.accessToken !== vinculatedToken) {
      // Conexão herdada (ex.: troca de usuário) ainda não foi vinculada ao token atual
      connection.invoke('VinculateUser', localStorage.accessToken).then(() => {
        vinculatedToken = localStorage.accessToken
      }).catch(() => {
        // Falha ao vincular usuário no hub é tratada na reconexão periódica
      })
    }
    return connection
  }

  if (isConnecting) {
    return connection || fallbackConnection
  }

  isConnecting = true
  try {
    if (connection && typeof connection.stop === 'function') {
      try {
        // isConnecting já está true: se o stop disparar onclose, rebootConnection sai sem reiniciar
        await connection.stop()
      } catch (e) {
        // Encerramento de conexão anterior zumbi é melhor esforço
      }
    }

    const newConnection = createConnection()
    connection = newConnection
    vinculatedToken = null
    setEventHandlers(newConnection)
    await startConnection(newConnection)

    if (localStorage.accessToken) {
      newConnection.invoke('VinculateUser', localStorage.accessToken).then(() => {
        vinculatedToken = localStorage.accessToken
      }).catch(() => {
        // Falha ao vincular usuário no hub é tratada na reconexão periódica
      })
    }

    // eslint-disable-next-line no-use-before-define
    newConnection.onclose(() => rebootConnection())
  } catch (err) {
    // Falha na inicialização da conexão agenda nova tentativa via rebootConnection
    // eslint-disable-next-line no-use-before-define
    setTimeout(() => rebootConnection(), 5000)
  } finally {
    isConnecting = false
  }

  return connection || fallbackConnection
}

// chamado quando acontece um erro ou quando o hub é desconectado
function rebootConnection() {
  if (isConnecting) return
  if (isConnectionActive(connection)) {
    return
  }
  start()
}

export async function stopEventHub() {
  if (connection && typeof connection.stop === 'function') {
    // isConnecting = true faz rebootConnection sair sem reiniciar quando o onclose deste stop disparar
    isConnecting = true
    try {
      await connection.stop()
    } catch (err) {
      // Encerramento seguro da conexão em melhor esforço
    } finally {
      connection = null
      isConnecting = false
      vinculatedToken = null
    }
  } else {
    connection = null
    isConnecting = false
    vinculatedToken = null
  }
}

export default function (startHub = true) {
  if (startHub) start()
  return connection || fallbackConnection
}
